/*
 * <bh-select> — accessible custom dropdown that progressively enhances a native
 * <select>. The native <select> stays in the DOM (hidden) so it keeps submitting the
 * form value AND so the configurator's JS can keep reading/writing it and repopulating
 * its <option>s dynamically (delivery zones, time slots). This component mirrors it as
 * a branded trigger + listbox panel, with optional type-ahead search for long lists.
 *
 * ARIA combobox/listbox pattern. Keyboard: Enter/Space/ArrowDown open; ArrowUp/Down move;
 * Enter select; Escape close; type to filter (when searchable). An <option> may carry
 * data-search with extra terms (e.g. its other-language name) to widen what matches.
 *
 * Markup:
 *   <bh-select data-placeholder="…" data-searchable="true">
 *     <select id="…" name="…"> … </select>
 *   </bh-select>
 */
class BhSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select || this._built) return;
    this._built = true;
    this.placeholder = this.getAttribute('data-placeholder') || '';
    this.searchable = this.getAttribute('data-searchable') === 'true';
    this.searchLabel = this.getAttribute('data-search-label') || '';
    this.activeIndex = -1;
    this._build();

    // Re-mirror when the configurator repopulates the native <select>'s options.
    this._mo = new MutationObserver(() => { if (!this._suppress) this._sync(); });
    this._mo.observe(this.select, { childList: true, subtree: true });
    // Reflect external value changes not initiated by this component.
    this.select.addEventListener('change', () => { if (!this._suppress) this._sync(); });

    this._onDocPointer = (e) => { if (!this.contains(e.target)) this._close(); };
  }

  disconnectedCallback() {
    this._mo?.disconnect();
    document.removeEventListener('pointerdown', this._onDocPointer);
  }

  _build() {
    this.select.classList.add('bh-select--native');
    this.select.setAttribute('tabindex', '-1');
    this.select.setAttribute('aria-hidden', 'true');

    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'bh-select__trigger';
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    const labelledby = this.select.getAttribute('aria-labelledby');
    if (labelledby) this.trigger.setAttribute('aria-labelledby', labelledby);

    this.panel = document.createElement('div');
    this.panel.className = 'bh-select__panel';
    this.panel.setAttribute('role', 'listbox');
    this.panel.hidden = true;

    if (this.searchable) {
      this.search = document.createElement('input');
      this.search.type = 'text';
      this.search.className = 'bh-select__search';
      this.search.setAttribute('autocomplete', 'off');
      // iOS autocorrect floats a suggestion bubble ("Студиен | Студено | Студена") right under
      // this field, over the left half of the first result. A tap on the result's label hit the
      // bubble instead and did nothing; only the empty right side worked (2026-10-11). Place
      // names gain nothing from autocorrect anyway.
      this.search.setAttribute('autocorrect', 'off');
      this.search.setAttribute('autocapitalize', 'off');
      this.search.setAttribute('spellcheck', 'false');
      if (this.searchLabel) this.search.setAttribute('placeholder', this.searchLabel);
      this.search.setAttribute('aria-label', this.searchLabel || this.placeholder || 'Search');
      this.search.addEventListener('input', () => this._filter());
      this.search.addEventListener('keydown', (e) => this._onPanelKey(e));
      this.panel.appendChild(this.search);
    }

    this.list = document.createElement('div');
    this.list.className = 'bh-select__list';
    this.list.addEventListener('keydown', (e) => this._onPanelKey(e));
    this.panel.appendChild(this.list);

    this.appendChild(this.trigger);
    this.appendChild(this.panel);

    this.trigger.addEventListener('click', () => this._toggle());
    this.trigger.addEventListener('keydown', (e) => this._onTriggerKey(e));

    this._sync();
  }

  /* mirror trigger label + option list from the native <select> */
  _sync() {
    const sel = this.select.selectedOptions[0];
    const hasValue = sel && sel.value !== '';
    this.trigger.innerHTML = '';
    const value = document.createElement('span');
    value.className = 'bh-select__value' + (hasValue ? '' : ' bh-select__value--placeholder');
    value.textContent = hasValue ? sel.textContent.trim() : (this.placeholder || (sel ? sel.textContent.trim() : ''));
    this.trigger.appendChild(value);
    const caret = document.createElement('span');
    caret.className = 'bh-select__caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML = '<svg viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    this.trigger.appendChild(caret);
    this._renderList(this.searchable && this.search ? this.search.value : '');
  }

  _renderList(filter) {
    const f = (filter || '').toLowerCase();
    this.list.innerHTML = '';
    const multi = this.select.options.length > 1;
    [...this.select.options].forEach((o, i) => {
      if (multi && o.value === '') return; // skip the placeholder option
      // Match the visible label OR the option's data-search haystack, so a list whose labels
      // are localised (delivery zones) stays searchable in either language regardless of the
      // storefront's current locale.
      if (f && !(o.dataset.search || o.textContent).toLowerCase().includes(f)) return;
      const opt = document.createElement('div');
      opt.className = 'bh-select__option';
      opt.setAttribute('role', 'option');
      opt.dataset.index = String(i);
      opt.textContent = o.textContent.trim();
      if (o.selected && o.value !== '') {
        opt.classList.add('is-selected');
        opt.setAttribute('aria-selected', 'true');
      }
      opt.addEventListener('click', () => this._pick(i));
      this.list.appendChild(opt);
    });
  }

  _filter() { this._renderList(this.search.value); this.activeIndex = -1; }

  _pick(i) {
    this._suppress = true;
    this.select.selectedIndex = i;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this._suppress = false;
    this._sync();
    this._close();
    this.trigger.focus();
  }

  _toggle() { this.hasAttribute('open') ? this._close() : this._open(); }

  _open() {
    if (!this.list.children.length) return;
    this.setAttribute('open', '');
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this._onDocPointer);
    this.activeIndex = -1;
    if (this.searchable && this.search) {
      this.search.value = '';
      this._renderList('');
      this.search.focus();
    } else {
      this._move(1);
    }
  }

  _close() {
    this.removeAttribute('open');
    this.panel.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this._onDocPointer);
  }

  _opts() { return [...this.list.querySelectorAll('.bh-select__option')]; }

  _onTriggerKey(e) {
    if (['ArrowDown', 'Enter', ' '].includes(e.key)) { e.preventDefault(); this._open(); }
  }

  _onPanelKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); this._move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this._move(-1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const opts = this._opts();
      if (this.activeIndex >= 0 && opts[this.activeIndex]) this._pick(parseInt(opts[this.activeIndex].dataset.index, 10));
    } else if (e.key === 'Escape') { e.preventDefault(); this._close(); this.trigger.focus(); }
    else if (e.key === 'Tab') { this._close(); }
  }

  _move(dir) {
    const opts = this._opts();
    if (!opts.length) return;
    if (this.activeIndex < 0) this.activeIndex = dir > 0 ? 0 : opts.length - 1;
    else this.activeIndex = Math.max(0, Math.min(opts.length - 1, this.activeIndex + dir));
    opts.forEach((o, i) => o.classList.toggle('is-active', i === this.activeIndex));
    opts[this.activeIndex].scrollIntoView({ block: 'nearest' });
  }
}

if (!customElements.get('bh-select')) customElements.define('bh-select', BhSelect);
